import { useEffect, useRef, useCallback } from 'react';

/**
 * useTracking — injects Facebook Pixel, TikTok Pixel, Google Tag Manager
 * and provides a trackEvent() helper to fire standard e-commerce events.
 *
 * @param {object} storeExtras - The store_extras object containing pixel IDs
 * @returns {{ trackEvent: (name: string, data?: object) => void }}
 */
export default function useTracking(storeExtras) {
  const injected = useRef({ fb: false, tt: false, gtm: false });

  const fbId = storeExtras?.fb_pixel_id || '';
  const ttId = storeExtras?.tiktok_pixel_id || '';
  const gtmId = storeExtras?.gtm_id || '';

  // ── Inject Facebook Pixel base code ──
  useEffect(() => {
    if (!fbId || injected.current.fb) return;
    injected.current.fb = true;

    /* Meta Pixel base code */
    const script = document.createElement('script');
    script.textContent = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${fbId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(fbId)}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  }, [fbId]);

  // ── Inject TikTok Pixel base code ──
  useEffect(() => {
    if (!ttId || injected.current.tt) return;
    injected.current.tt = true;

    const script = document.createElement('script');
    script.textContent = `
      !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
      ttq.methods=["page","track","identify","instances","debug","on","off",
      "once","ready","alias","group","enableCookie","disableCookie"];
      ttq.setAndDefer=function(t,e){t[e]=function(){
      t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
      for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
      ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)
      ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){
      var i="https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;
      ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};
      ttq._o[e]=n||{};var o=document.createElement("script");
      o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e+"&lib="+t;
      var a=document.getElementsByTagName("script")[0];
      a.parentNode.insertBefore(o,a)};
      ttq.load('${ttId}');ttq.page();
      }(window,document,'ttq');
    `;
    document.head.appendChild(script);
  }, [ttId]);

  // ── Inject Google Tag Manager ──
  useEffect(() => {
    if (!gtmId || injected.current.gtm) return;
    injected.current.gtm = true;

    window.dataLayer = window.dataLayer || [];

    const script = document.createElement('script');
    script.textContent = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
      f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }, [gtmId]);

  // ── Event mapping ──
  const trackEvent = useCallback((eventName, data = {}) => {
    // Facebook Pixel
    if (fbId && typeof window.fbq === 'function') {
      const fbMap = {
        PageView: () => window.fbq('track', 'PageView'),
        ViewContent: () => window.fbq('track', 'ViewContent', {
          content_name: data.name,
          content_ids: data.id ? [data.id] : undefined,
          content_type: 'product',
          value: data.price,
          currency: 'IDR',
        }),
        AddToCart: () => window.fbq('track', 'AddToCart', {
          content_name: data.name,
          content_ids: data.id ? [data.id] : undefined,
          content_type: 'product',
          value: data.price,
          currency: 'IDR',
        }),
        InitiateCheckout: () => window.fbq('track', 'InitiateCheckout', {
          value: data.value,
          currency: 'IDR',
          num_items: data.num_items,
        }),
        Purchase: () => window.fbq('track', 'Purchase', {
          value: data.value,
          currency: 'IDR',
          content_type: 'product',
          num_items: data.num_items,
        }),
      };
      fbMap[eventName]?.();
    }

    // TikTok Pixel
    if (ttId && typeof window.ttq?.track === 'function') {
      const ttMap = {
        PageView: () => window.ttq.page(),
        ViewContent: () => window.ttq.track('ViewContent', {
          content_id: data.id,
          content_name: data.name,
          content_type: 'product',
          value: data.price,
          currency: 'IDR',
        }),
        AddToCart: () => window.ttq.track('AddToCart', {
          content_id: data.id,
          content_name: data.name,
          content_type: 'product',
          value: data.price,
          currency: 'IDR',
        }),
        InitiateCheckout: () => window.ttq.track('InitiateCheckout', {
          value: data.value,
          currency: 'IDR',
        }),
        Purchase: () => window.ttq.track('CompletePayment', {
          value: data.value,
          currency: 'IDR',
        }),
      };
      ttMap[eventName]?.();
    }

    // Google Tag Manager dataLayer
    if (gtmId) {
      window.dataLayer = window.dataLayer || [];
      const gtmMap = {
        PageView: () => window.dataLayer.push({ event: 'page_view' }),
        ViewContent: () => window.dataLayer.push({
          event: 'view_item',
          ecommerce: {
            items: [{ item_id: data.id, item_name: data.name, price: data.price, currency: 'IDR' }],
          },
        }),
        AddToCart: () => window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            items: [{ item_id: data.id, item_name: data.name, price: data.price, currency: 'IDR', quantity: data.quantity || 1 }],
          },
        }),
        InitiateCheckout: () => window.dataLayer.push({
          event: 'begin_checkout',
          ecommerce: { value: data.value, currency: 'IDR', items: data.items || [] },
        }),
        Purchase: () => window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: data.order_id,
            value: data.value,
            currency: 'IDR',
            items: data.items || [],
          },
        }),
      };
      gtmMap[eventName]?.();
    }
  }, [fbId, ttId, gtmId]);

  return { trackEvent };
}
